/**
 * Platform-agnostic core of the event tracker: a persisted queue with batching, retry and an
 * overflow cap. The web wrapper (eventTracker.ts) supplies storage (localStorage), the transport (fetch keepalive)
 * and lifecycle hooks (visibilitychange/pagehide); this file has no DOM access so it is fully unit-tested.
 * It is a verbatim copy of espotz-mobile-app/src/shared/analytics/eventTracker/eventQueue.ts — keep them in sync.
 *
 * Delivery contract with the collector (POST /events):
 *   202 → every event in the batch is done, accepted AND rejected (rejects are bugs to log, not retry)
 *   400 → the batch itself is malformed; drop it and report, retrying would loop forever
 *   503 / network error → keep the batch, back off exponentially (30 s → 5 min cap)
 *
 * Why persist on every track(): impressions and dwell views fire exactly when the user is about to
 * leave — background, swipe-away, crash. A memory-only queue loses the events we care about most.
 */
import type { CollectorResponse, EventEnvelope } from './types';

export interface QueueStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export type SendResult =
  | { kind: 'accepted'; response: CollectorResponse }
  | { kind: 'client_error'; status: number; body?: unknown }
  | { kind: 'retry'; status?: number; error?: unknown };

export interface EventQueueOptions {
  storage: QueueStorage;
  send: (events: EventEnvelope[]) => Promise<SendResult>;
  /** Events per request; the collector caps at 100. */
  batchSize?: number;
  /** Queue cap; oldest are dropped past this and the drop is reported on the next event. */
  maxQueued?: number;
  /** Backoff after a retryable failure. */
  minBackoffMs?: number;
  maxBackoffMs?: number;
  now?: () => number;
  onRejected?: (rejected: CollectorResponse['rejected']) => void;
  onClientError?: (status: number, body: unknown) => void;
  onError?: (message: string, error?: unknown) => void;
  log?: (message: string, data?: unknown) => void;
}

export const QUEUE_KEY = 'analytics.queue.v1';
export const DROPPED_KEY = 'analytics.dropped.v1';

const DEFAULTS = {
  batchSize: 50,
  maxQueued: 1000,
  minBackoffMs: 30_000,
  maxBackoffMs: 5 * 60_000,
};

export class EventQueue {
  private readonly opts: Required<
    Pick<
      EventQueueOptions,
      'batchSize' | 'maxQueued' | 'minBackoffMs' | 'maxBackoffMs'
    >
  > &
    EventQueueOptions;
  private queue: EventEnvelope[];
  private droppedSinceLastReport: number;
  private inFlight = false;
  private backoffUntil = 0;
  private consecutiveFailures = 0;

  constructor(options: EventQueueOptions) {
    this.opts = { ...DEFAULTS, ...options };
    this.queue = this.loadQueue();
    this.droppedSinceLastReport = Number(this.safeGet(DROPPED_KEY) ?? 0) || 0;
  }

  get size(): number {
    return this.queue.length;
  }

  /** Append and persist. Returns true when the queue has reached a full batch. */
  enqueue(event: EventEnvelope): boolean {
    if (this.droppedSinceLastReport > 0) {
      // Report the drop on the next event so an overflow is never silent (no silent caps).
      event = {
        ...event,
        metadata: {
          ...(event.metadata ?? {}),
          droppedCount: this.droppedSinceLastReport,
        },
      };
      this.droppedSinceLastReport = 0;
      this.safeRemove(DROPPED_KEY);
    }
    this.queue.push(event);
    if (this.queue.length > this.opts.maxQueued) {
      const overflow = this.queue.length - this.opts.maxQueued;
      this.queue.splice(0, overflow);
      this.droppedSinceLastReport += overflow;
      this.safeSet(DROPPED_KEY, String(this.droppedSinceLastReport));
      this.opts.log?.('[events] queue overflow, dropped oldest', { overflow });
    }
    this.persist();
    return this.queue.length >= this.opts.batchSize;
  }

  /**
   * Send one batch if there is anything to send, nothing is in flight, and we are not backing off.
   * Resolves once that batch is settled. `force` ignores the backoff (used on app background,
   * where this may be the last chance to deliver).
   */
  async flush(force = false): Promise<void> {
    if (this.inFlight || this.queue.length === 0) return;
    const now = (this.opts.now ?? Date.now)();
    if (!force && now < this.backoffUntil) return;

    const batch = this.queue.slice(0, this.opts.batchSize);
    this.inFlight = true;
    try {
      const result = await this.opts.send(batch);
      switch (result.kind) {
        case 'accepted': {
          // Accepted and rejected alike are done — the collector judged them; a rejected event is a
          // client bug (unknown name, missing entity) that retrying cannot fix.
          this.removeBatch(batch);
          this.consecutiveFailures = 0;
          this.backoffUntil = 0;
          if (result.response.rejected.length > 0)
            this.opts.onRejected?.(result.response.rejected);
          break;
        }
        case 'client_error': {
          this.removeBatch(batch);
          this.opts.onClientError?.(result.status, result.body);
          break;
        }
        case 'retry': {
          this.consecutiveFailures += 1;
          const backoff = Math.min(
            this.opts.maxBackoffMs,
            this.opts.minBackoffMs * 2 ** (this.consecutiveFailures - 1),
          );
          this.backoffUntil = now + backoff;
          this.opts.log?.('[events] delivery failed, keeping batch', {
            status: result.status,
            backoffMs: backoff,
          });
          break;
        }
      }
    } catch (error) {
      // `send` is expected to classify errors itself; anything that escapes is treated as retryable.
      this.consecutiveFailures += 1;
      this.backoffUntil =
        now +
        Math.min(
          this.opts.maxBackoffMs,
          this.opts.minBackoffMs * 2 ** (this.consecutiveFailures - 1),
        );
      this.opts.onError?.('[events] unexpected send failure', error);
    } finally {
      this.inFlight = false;
    }
  }

  /** Keep flushing while full batches remain and the last one succeeded (used on foreground/boot). */
  async drain(force = false): Promise<void> {
    let before = this.queue.length;
    while (before > 0) {
      await this.flush(force);
      if (this.queue.length >= before) return; // nothing was removed → stop (backoff or failure)
      before = this.queue.length;
    }
  }

  /** Test/debug hook. */
  peek(): readonly EventEnvelope[] {
    return this.queue;
  }

  private removeBatch(batch: EventEnvelope[]): void {
    const ids = new Set(batch.map(e => e.eventId));
    this.queue = this.queue.filter(e => !ids.has(e.eventId));
    this.persist();
  }

  private loadQueue(): EventEnvelope[] {
    const raw = this.safeGet(QUEUE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed)
        ? (parsed as EventEnvelope[]).filter(
            e => e && typeof e.eventId === 'string',
          )
        : [];
    } catch {
      this.safeRemove(QUEUE_KEY);
      return [];
    }
  }

  private persist(): void {
    if (this.queue.length === 0) this.safeRemove(QUEUE_KEY);
    else this.safeSet(QUEUE_KEY, JSON.stringify(this.queue));
  }

  // Storage can throw (full disk, private mode on web); analytics must never take the app down.
  private safeGet(key: string): string | null {
    try {
      return this.opts.storage.get(key);
    } catch {
      return null;
    }
  }
  private safeSet(key: string, value: string): void {
    try {
      this.opts.storage.set(key, value);
    } catch (error) {
      this.opts.onError?.('[events] storage write failed', error);
    }
  }
  private safeRemove(key: string): void {
    try {
      this.opts.storage.remove(key);
    } catch {
      /* ignore */
    }
  }
}

/** Session rotates after this long in the background / idle. */
export const SESSION_IDLE_MS = 30 * 60_000;

export const ANON_ID_KEY = 'analytics.anonId.v1';
export const SESSION_KEY = 'analytics.session.v1';

export interface SessionState {
  sessionId: string;
  lastActiveMs: number;
}

/** Stable per-install id; survives logout on purpose (D15 stitching happens server-side). */
export function getOrCreateAnonId(
  storage: QueueStorage,
  newId: () => string,
): string {
  try {
    const existing = storage.get(ANON_ID_KEY);
    if (existing) return existing;
  } catch {
    /* fall through */
  }
  const id = newId();
  try {
    storage.set(ANON_ID_KEY, id);
  } catch {
    /* memory-only is fine */
  }
  return id;
}

/**
 * Returns the current session id, rotating it when the previous activity is older than the idle
 * window. `isNew` is true when this call minted it — the moment a session starts.
 */
export function resolveSession(
  storage: QueueStorage,
  newId: () => string,
  nowMs: number,
  idleMs: number = SESSION_IDLE_MS,
): { sessionId: string; isNew: boolean } {
  let state: SessionState | null = null;
  try {
    const raw = storage.get(SESSION_KEY);
    state = raw ? (JSON.parse(raw) as SessionState) : null;
  } catch {
    state = null;
  }
  const expired =
    !state ||
    typeof state.sessionId !== 'string' ||
    nowMs - state.lastActiveMs > idleMs;
  const sessionId = expired ? newId() : (state as SessionState).sessionId;
  try {
    storage.set(
      SESSION_KEY,
      JSON.stringify({ sessionId, lastActiveMs: nowMs } satisfies SessionState),
    );
  } catch {
    /* ignore */
  }
  return { sessionId, isNew: expired };
}

/** UUID v4 with a fallback for runtimes without `crypto.randomUUID` (old browsers, jsdom). */
export function newUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    return crypto.randomUUID();
  // RFC 4122 v4 layout from Math.random — not cryptographic, fine for an event id.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
